import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, Loader2, Edit, Check, X, CreditCard, ListChecks,
  GripVertical,
} from 'lucide-react';

const SubscriptionTierManagement = () => {
  const { api } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editTier, setEditTier] = useState(null);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [tab, setTab] = useState('tiers');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, f] = await Promise.all([
        api.get('/admin/subscription-tiers'),
        api.get('/admin/subscription-tiers/features/list'),
      ]);
      setTiers(t.data || []);
      setFeatures(f.data || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // Tier CRUD
  const saveTier = async () => {
    if (!editTier?.name?.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...editTier, price_monthly: Number(editTier.price_monthly), price_yearly: Number(editTier.price_yearly), user_limit: Number(editTier.user_limit) };
      if (editTier.id && !editTier._isNew) {
        await api.put(`/admin/subscription-tiers/${editTier.id}`, payload);
        toast.success('Tier updated');
      } else {
        delete payload.id; delete payload._isNew;
        await api.post('/admin/subscription-tiers', payload);
        toast.success('Tier created');
      }
      setShowTierDialog(false); setEditTier(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteTier = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    try { await api.delete(`/admin/subscription-tiers/${id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const toggleFeature = (featureName) => {
    if (!editTier) return;
    const has = editTier.included_features?.includes(featureName);
    setEditTier(prev => ({
      ...prev,
      included_features: has
        ? prev.included_features.filter(f => f !== featureName)
        : [...(prev.included_features || []), featureName],
    }));
  };

  // Feature list management
  const addFeature = () => {
    if (!newFeature.trim()) return;
    if (features.some(f => f.name === newFeature.trim())) { toast.error('Feature already exists'); return; }
    setFeatures(prev => [...prev, { id: `new-${Date.now()}`, name: newFeature.trim(), order: prev.length }]);
    setNewFeature('');
  };

  const removeFeature = (id) => setFeatures(prev => prev.filter(f => f.id !== id));

  const saveFeatures = async () => {
    setSaving(true);
    try {
      await api.post('/admin/subscription-tiers/features', features);
      toast.success('Features saved');
      fetchAll();
    } catch { toast.error('Failed to save features'); }
    finally { setSaving(false); }
  };

  const openNewTier = () => {
    setEditTier({ _isNew: true, name: '', user_limit: 10, price_monthly: 0, price_yearly: 0, description: '', included_features: [], is_active: true, is_custom: false, order: tiers.length });
    setShowTierDialog(true);
  };

  const openEditTier = (tier) => {
    setEditTier({ ...tier });
    setShowTierDialog(true);
  };

  if (loading) return <DashboardLayout requiredRoles={['super_admin']}><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div></DashboardLayout>;

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="tier-management">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Subscription Tiers</h1>
            <p className="text-slate-500 mt-1">Manage pricing plans and features</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="tiers" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5">
              <CreditCard className="h-3.5 w-3.5" /> Tiers
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5">
              <ListChecks className="h-3.5 w-3.5" /> Features
            </TabsTrigger>
          </TabsList>

          {/* ─── TIERS TAB ─── */}
          <TabsContent value="tiers">
            <div className="flex justify-end mb-4">
              <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={openNewTier} data-testid="create-tier-btn">
                <Plus className="h-4 w-4 mr-2" /> New Tier
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tiers.map(tier => (
                <Card key={tier.id} className={`border-slate-200 ${!tier.is_active ? 'opacity-60' : ''}`} data-testid={`tier-card-${tier.id}`}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{tier.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{tier.description}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditTier(tier)} data-testid={`edit-tier-${tier.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteTier(tier.id)} data-testid={`del-tier-${tier.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                      </div>
                    </div>
                    {tier.is_custom ? (
                      <p className="text-2xl font-bold text-[#20acf6]">Custom</p>
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-[#20acf6]">£{tier.price_monthly}<span className="text-sm font-normal text-slate-400">/mo</span></p>
                        <p className="text-xs text-slate-500">£{tier.price_yearly}/year</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Badge variant="outline" className="text-[10px]">{tier.user_limit >= 9999 ? '500+' : `Up to ${tier.user_limit}`} staff</Badge>
                      <Badge variant="outline" className="text-[10px]">{tier.included_features?.length || 0} features</Badge>
                      {!tier.is_active && <Badge className="bg-red-100 text-red-600 text-[10px]">Inactive</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ─── FEATURES TAB ─── */}
          <TabsContent value="features">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Feature List</CardTitle>
                <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={saveFeatures} disabled={saving} data-testid="save-features-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Features
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input value={newFeature} onChange={e => setNewFeature(e.target.value)} placeholder="New feature name..." onKeyDown={e => e.key === 'Enter' && addFeature()} data-testid="new-feature-input" />
                  <Button variant="outline" onClick={addFeature} data-testid="add-feature-btn"><Plus className="h-4 w-4" /></Button>
                </div>
                <p className="text-xs text-slate-500">Drag features to reorder. Order here matches the public pricing page.</p>
                {features.map((f, i) => (
                  <div
                    key={f.id}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', i.toString()); e.currentTarget.classList.add('opacity-50'); }}
                    onDragEnd={(e) => { e.currentTarget.classList.remove('opacity-50'); }}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-[#20acf6]'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-[#20acf6]'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('ring-2', 'ring-[#20acf6]');
                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                      if (isNaN(fromIdx) || fromIdx === i) return;
                      setFeatures(prev => {
                        const next = [...prev];
                        const [moved] = next.splice(fromIdx, 1);
                        next.splice(i, 0, moved);
                        return next.map((feat, idx) => ({ ...feat, order: idx }));
                      });
                    }}
                    className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2 cursor-grab active:cursor-grabbing transition-all"
                    data-testid={`feature-item-${i}`}
                  >
                    <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="flex-1 text-sm text-slate-800">{f.name}</span>
                    <span className="text-[10px] text-slate-400 mr-2">#{i + 1}</span>
                    <Button size="icon" variant="ghost" onClick={() => removeFeature(f.id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ─── TIER EDIT DIALOG ─── */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tier-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editTier?._isNew ? 'Create Tier' : 'Edit Tier'}</DialogTitle>
          </DialogHeader>
          {editTier && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={editTier.name} onChange={e => setEditTier(p => ({...p, name: e.target.value}))} data-testid="tier-name-input" /></div>
                <div><Label>Staff Limit</Label><Input type="number" value={editTier.user_limit} onChange={e => setEditTier(p => ({...p, user_limit: e.target.value}))} data-testid="tier-user-limit" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Monthly Price (£)</Label><Input type="number" step="0.01" value={editTier.price_monthly} onChange={e => setEditTier(p => ({...p, price_monthly: e.target.value}))} data-testid="tier-price-monthly" /></div>
                <div><Label>Annual Price (£)</Label><Input type="number" step="0.01" value={editTier.price_yearly} onChange={e => setEditTier(p => ({...p, price_yearly: e.target.value}))} data-testid="tier-price-yearly" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={editTier.description || ''} onChange={e => setEditTier(p => ({...p, description: e.target.value}))} rows={2} /></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><Switch checked={editTier.is_active} onCheckedChange={v => setEditTier(p => ({...p, is_active: v}))} /><Label>Active</Label></div>
                <div className="flex items-center gap-2"><Switch checked={editTier.is_custom} onCheckedChange={v => setEditTier(p => ({...p, is_custom: v}))} /><Label>Custom pricing</Label></div>
              </div>
              {/* Feature toggles with drag-and-drop ordering */}
              <div>
                <Label className="mb-2 block text-sm font-semibold">Included Features</Label>
                <p className="text-xs text-slate-500 mb-2">Toggle features on/off. Drag included features to reorder. This order appears on the pricing page.</p>
                {/* Included features - draggable to reorder */}
                {(editTier.included_features?.length || 0) > 0 && (
                  <div className="mb-3 bg-[#20acf6]/5 rounded-lg p-3 space-y-1" data-testid="included-features-sortable">
                    <p className="text-xs font-medium text-[#20acf6] mb-1">Included (drag to reorder):</p>
                    {editTier.included_features.map((fname, idx) => (
                      <div
                        key={fname}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', idx.toString()); e.currentTarget.classList.add('opacity-50'); }}
                        onDragEnd={(e) => { e.currentTarget.classList.remove('opacity-50'); }}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-[#20acf6]'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-[#20acf6]'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('ring-2', 'ring-[#20acf6]');
                          const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                          if (isNaN(fromIdx) || fromIdx === idx) return;
                          setEditTier(prev => {
                            const next = [...(prev.included_features || [])];
                            const [moved] = next.splice(fromIdx, 1);
                            next.splice(idx, 0, moved);
                            return { ...prev, included_features: next };
                          });
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm cursor-grab active:cursor-grabbing border border-[#20acf6]/20 transition-all"
                        data-testid={`tier-feat-drag-${idx}`}
                      >
                        <GripVertical className="h-3.5 w-3.5 text-[#20acf6]/50 shrink-0" />
                        <span className="flex-1 text-[#2a3e5d] font-medium">{fname}</span>
                        <span className="text-[10px] text-slate-400 mr-1">#{idx + 1}</span>
                        <button onClick={() => toggleFeature(fname)} className="text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Available features to toggle */}
                <div className="space-y-1.5 max-h-48 overflow-y-auto bg-slate-50 rounded-lg p-3">
                  {features.filter(f => !editTier.included_features?.includes(f.name)).map(f => (
                    <button
                      key={f.id}
                      onClick={() => toggleFeature(f.name)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors text-slate-600 hover:bg-slate-100"
                      data-testid={`toggle-feat-${f.name.replace(/\s/g, '-')}`}
                    >
                      <div className="w-5 h-5 rounded-md border flex items-center justify-center shrink-0 border-slate-300">
                      </div>
                      {f.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={saveTier} disabled={saving} data-testid="save-tier-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SubscriptionTierManagement;
